/**
 * One store per open chart settings popup. Committed settings have a 50-step history; hover previews
 * (type, color, theme) are layered on top and never enter history. Drafts autosave 600 ms after the last
 * change (motion.ts `autosave`). Nothing reaches the overview until the dialog's Add / Save.
 */
import { createStore } from "zustand/vanilla";
import { subscribeWithSelector } from "zustand/middleware";
import { useStore } from "zustand";
import { recipes } from "../../motion";
import { describeChanges, type ChartSettings, type ColorSettings } from "./chartSettings";
import type { ChartType } from "./chartTypes";

export type SettingsTab = "data" | "chart" | "labels" | "colors" | "interact";
export interface HistoryStep { settings: ChartSettings; label: string; at: number }
export interface Preview { type?: ChartType; colors?: ColorSettings; themeId?: string }

export interface ChartSettingsState {
  settings: ChartSettings;
  preview: Preview | null;
  past: HistoryStep[];
  future: HistoryStep[];
  tab: SettingsTab;
  save: "idle" | "saving" | "saved" | "error";
  savedAt: number | null;

  setTab: (t: SettingsTab) => void;
  /** One undoable change. `label` shows in History; omit it to derive one from the diff. */
  update: (fn: (s: ChartSettings) => ChartSettings, label?: string) => void;
  undo: () => void;
  redo: () => void;
  setPreview: (p: Preview | null) => void;
  /** "Space to try" / clicking a hovered swatch: keep the preview as a real step. */
  commitPreview: () => void;
}

export interface StoreDeps {
  saveDraft: (s: ChartSettings) => Promise<void>;
  schedule?: (fn: () => void, ms: number) => unknown;
  cancel?: (h: unknown) => void;
  now?: () => number;
}

export const effectiveSettings = (st: Pick<ChartSettingsState, "settings" | "preview">): ChartSettings => {
  const p = st.preview;
  if (!p) return st.settings;
  return { ...st.settings, type: p.type ?? st.settings.type, colors: p.colors ?? st.settings.colors, themeId: p.themeId ?? st.settings.themeId };
};

export function createChartSettingsStore(initial: ChartSettings, deps: StoreDeps) {
  const schedule = deps.schedule ?? ((fn, ms) => setTimeout(fn, ms));
  const cancel = deps.cancel ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  const now = deps.now ?? Date.now;
  const LIMIT = recipes.autosave.historySteps;
  let timer: unknown = null;

  return createStore<ChartSettingsState>()(subscribeWithSelector((set, get) => {
    const queueSave = () => {
      if (timer) cancel(timer);
      timer = schedule(async () => {
        timer = null;
        set({ save: "saving" });
        try { await deps.saveDraft(get().settings); set({ save: "saved", savedAt: now() }); }
        catch { set({ save: "error" }); }
      }, recipes.autosave.debounceMs);
    };
    const push = (next: ChartSettings, label?: string) => {
      const prev = get().settings;
      if (JSON.stringify(prev) === JSON.stringify(next)) return;
      const step: HistoryStep = { settings: prev, label: label ?? describeChanges(prev, next)[0] ?? "Change", at: now() };
      set({ settings: next, past: [...get().past, step].slice(-LIMIT), future: [], preview: null });
      queueSave();
    };
    return {
      settings: initial, preview: null, past: [], future: [], tab: "chart", save: "idle", savedAt: null,
      setTab: (tab) => set({ tab }),
      update: (fn, label) => push(fn(get().settings), label),
      undo: () => {
        const { past, settings, future } = get();
        const prev = past.at(-1);
        if (!prev) return;
        set({ settings: prev.settings, past: past.slice(0, -1), future: [{ settings, label: prev.label, at: now() }, ...future], preview: null });
        queueSave();
      },
      redo: () => {
        const { past, settings, future } = get();
        const next = future[0];
        if (!next) return;
        set({ settings: next.settings, future: future.slice(1), past: [...past, { settings, label: next.label, at: now() }].slice(-LIMIT), preview: null });
        queueSave();
      },
      setPreview: (preview) => set({ preview }),
      commitPreview: () => { const p = get().preview; if (p) push(effectiveSettings(get()), p.type ? `Type: ${p.type}` : p.themeId ? `Theme: ${p.themeId}` : "Colors"); },
    };
  }));
}

export type ChartSettingsStore = ReturnType<typeof createChartSettingsStore>;
export const useChartSettings = <T,>(store: ChartSettingsStore, sel: (s: ChartSettingsState) => T) => useStore(store, sel);
